import React from "react";
import PropTypes from "prop-types";
import { cmsTextClass, sheetClass } from "../../styles";

/**
 * Site1 footer: the business's mark, its social links and the copyright line —
 * and, on the free tier, the platform's "Made with EasyPlanner" line.
 *
 * Always rendered, unlike the sections above it: the name and the year are
 * never blank, and the badge is a condition of the plan rather than a piece of
 * the operator's content, so it may not disappear with the social links.
 */

// Social URLs are operator-supplied but still untrusted input: `javascript:`
// and `data:` in an href execute on click. Only http(s) links are rendered.
export const safeHref = (url) => {
  if (!url) return null;
  try {
    const parsed = new URL(url, window.location.origin);
    return ["http:", "https:"].includes(parsed.protocol) ? parsed.href : null;
  } catch {
    return null;
  }
};

const socialLinkClass =
  "flex size-11 items-center justify-center rounded-full border border-ink-200 text-ink-700 transition-colors hover:border-[var(--site1-primary)] hover:text-[var(--site1-primary-strong)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--site1-primary-strong)] motion-reduce:transition-none";

const NETWORKS = [
  { key: "facebookUrl", label: "Facebook", icon: "bi-facebook" },
  { key: "instagramUrl", label: "Instagram", icon: "bi-instagram" },
];

const Footer = ({ businessName, logo = null, social = {}, showsBadge = false }) => {
  const links = NETWORKS.map((network) => ({
    ...network,
    href: safeHref(social[network.key]),
  })).filter((network) => network.href);

  return (
    <footer className="border-t border-ink-100 bg-white">
      <div className={`${sheetClass} flex flex-col items-center gap-5 py-8 sm:flex-row sm:justify-between`}>
        {logo ? (
          <img src={logo} alt={businessName} className="max-h-10 w-auto max-w-[10rem] object-contain" />
        ) : (
          <p className={`${cmsTextClass} m-0 text-center text-lg font-bold text-[var(--site1-heading)] sm:text-left`}>
            {businessName}
          </p>
        )}

        {links.length > 0 && (
          <ul className="m-0 flex list-none gap-2 p-0">
            {links.map((network) => (
              <li key={network.key}>
                <a
                  className={socialLinkClass}
                  href={network.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={network.label}
                >
                  <i className={`bi ${network.icon} text-lg`} aria-hidden="true" />
                </a>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className={sheetClass}>
        <div className="flex flex-col items-center gap-2 border-t border-ink-100 py-5 text-center text-fluid-sm text-ink-500 sm:flex-row sm:justify-between sm:text-left">
          <p className={`${cmsTextClass} m-0`}>
            © {new Date().getFullYear()} {businessName}
          </p>
          {showsBadge && (
            <a
              href="/"
              className="rounded underline-offset-2 transition-colors hover:text-ink-900 hover:underline focus-visible:text-ink-900 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--site1-primary-strong)]"
            >
              Made with EasyPlanner
            </a>
          )}
        </div>
      </div>
    </footer>
  );
};

Footer.propTypes = {
  businessName: PropTypes.string,
  logo: PropTypes.string,
  social: PropTypes.shape({
    facebookUrl: PropTypes.string,
    instagramUrl: PropTypes.string,
  }),
  showsBadge: PropTypes.bool,
};

export default Footer;
